import { SCRIPT_URL } from '@marsidev/react-turnstile'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { NextSeo } from 'next-seo'
import Head from 'next/head'
import Router, { useRouter } from 'next/router'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { isBrowser } from 'react-device-detect'
import { AnyObject, Form } from 'react-final-form'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faEye } from '@fortawesome/pro-light-svg-icons/faEye'
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash'
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird'
import { FORM_ERROR, SubmissionErrors } from 'final-form'
import signCsrfToken from 'server/csrf'
import { object } from 'yup'

import { DEVELOPER_DASHBOARD_URL, FEATURE_FLAGS } from '@infura/packages.common/constants'
import { trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'
import { needsMfa } from '@infura/packages.common/user'
import { Analytics } from '@infura/packages.components/Analytics'
import Notification from '@infura/packages.components/Notification'
import { Button } from '@infura/packages.ui/Button'

import { AuthLayout } from 'components/AuthLayout'
import { CaptchaModal } from 'components/CaptchaModal'
import FieldInput from 'components/FieldInput'
import { Footer } from 'components/loginRegistration/footer'
import RecoverMFA from 'components/mfa/RecoverMFA'
import ResetMFA from 'components/mfa/ResetMFA'
import VerifyMFAForm from 'components/mfa/VerifyMFA'
import LoginWalletLinkingForm from 'components/wallet/LoginWalletLinkingForm'
import UsersWalletPairingForm from 'components/wallet/UsersWalletPairingForm'
import WalletLoginForm from 'components/wallet/WalletLoginForm'

import useUser from 'hooks/useUser'

import FeatureToggle from 'components/FeatureToggle'
import { Separator } from 'components/separator/Separator'
import { SocialAuthForm } from 'components/socialAuthForm'
import { api } from 'lib/api/api'
import { LOGIN_METHOD_NAME } from 'lib/constants'
import { emailValidator, getValidationErrors, passwordValidator } from 'lib/forms/validators'
import handleSubmissionError from 'lib/handleSubmissionError'
import { isLocalUrl, isPermittedUrl } from 'lib/routeUtils'
import { AUTH_WALLET_PAIRING, AUTH_WALLET_SESSION_NAME, authenticateAndAuthorize } from 'lib/siwsrp/auth'
import { Infura } from 'lib/types/infuraTypes'

const validationSchema = object().shape({
  email: emailValidator,
  password: passwordValidator
})

function LoginSubmission({ submitting, valid }: { valid: boolean; submitting: boolean }) {
  const { query } = useRouter()
  const [captchaModalOpen, setCaptchaModalOpen] = useState(false)

  return !query.bypass ? (
    <>
      <Button
        className='mb-0 min-w-20'
        data-testid='auth-button'
        disabled={submitting}
        loading={submitting}
        onClick={() => {
          if (valid) {
            sessionStorage.setItem(LOGIN_METHOD_NAME, 'Email/Password')
            setCaptchaModalOpen(true)
          }
        }}
        type='button'
      >
        Sign in with email
      </Button>
      <CaptchaModal open={captchaModalOpen} setIsOpen={setCaptchaModalOpen} />
    </>
  ) : (
    <Analytics
      action='click'
      event={{
        href: `${DEVELOPER_DASHBOARD_URL}/login`,
        location: 'Dashboard Login Page',
        text: 'Login',
        type: 'Button'
      }}
    >
      <Button className='mb-8 w-full' data-testid='auth-button' disabled={submitting} type='submit'>
        <span className='xxl:text-lg text-base'>
          {submitting ? <FontAwesomeIcon className='fa-fw fa-spin' icon={faSpinnerThird} /> : 'Login with email'}
        </span>
      </Button>
    </Analytics>
  )
}

type OnSubmitProps =
  | { email: string; password: string; recaptcha: string; authLoginMethod: Infura.AUTH_LOGIN_METHOD.EMAIL_PASSWORD }
  | { authLoginMethod: Infura.AUTH_LOGIN_METHOD.WALLET }

type LoginFormProps = {
  onSubmit: (props: OnSubmitProps) => Promise<boolean | AnyObject | SubmissionErrors>
  redirectTo?: string
  csrfToken: string
}

const LoginForm = ({ csrfToken, onSubmit, redirectTo }: LoginFormProps) => {
  const [passwordVisible, setPasswordVisible] = useState(false)
  const flags = useFlags()
  const isMetamaskAvailable = useMemo(() => flags[FEATURE_FLAGS.METAMASK_LOGIN], [flags])
  const [isTracked, setIsTracked] = useState(false)
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    if (window?.analytics && isTracked) {
      window.analytics.track('Login Initiated', {
        login_method: 'Email/Password',
        timestamp: Date.now(),
        user_experience: isMetamaskAvailable ? 'B' : 'A'
      })
    }
  }, [isTracked, isMetamaskAvailable])

  const validateLogin = async values => {
    return await getValidationErrors({
      validationSchema,
      values
    })
  }

  const initClickEventProps = {
    location: 'Dashboard Login Page',
    type: 'Text'
  }
  const registerLink = redirectTo ? `/register?redirect_to=${encodeURIComponent(redirectTo)}` : '/register'

  const handleFormSubmit = (values: OnSubmitProps) => {
    setError(undefined)
    return onSubmit(values)
  }

  return (
    <div>
      <h1 className='font-body xxl:text-4xl text-text-default mb-8 text-3xl font-bold'>Sign in</h1>

      <div className='flex-between flex flex-grow flex-row gap-4 pb-4'>
        <div className='w-full justify-start'>
          <Form
            initialValues={{ authLoginMethod: Infura.AUTH_LOGIN_METHOD.EMAIL_PASSWORD }}
            onSubmit={handleFormSubmit}
            render={({ dirty, handleSubmit, hasValidationErrors, submitError, submitting }) => {
              if (dirty && !isTracked) setIsTracked(true)

              const displayedError = submitError || error

              return (
                <Fragment>
                  {displayedError ? (
                    <Notification
                      className='mb-6'
                      color='alert'
                      data-testid='auth-form-error'
                      size='sm'
                      variant='outlined'
                    >
                      <span className='w-full text-center'>{displayedError}</span>
                    </Notification>
                  ) : null}
                  <div>
                    {isMetamaskAvailable && isBrowser ? (
                      <>
                        <div className='flex justify-start'>
                          <WalletLoginForm onError={setError} onSubmit={handleFormSubmit} />
                        </div>
                        <Separator className='my-4' label='or' />
                      </>
                    ) : null}
                    <FeatureToggle flag={FEATURE_FLAGS.WEB3AUTH_LOGIN_AND_REGISTRATION} waitUntilFlagReady>
                      <>
                        <SocialAuthForm csrfToken={csrfToken} onError={setError} />
                        <Separator className='my-4' label='or' />
                      </>
                    </FeatureToggle>
                  </div>
                  <form className='xxl:text-lg flex-grow text-base' data-testid='auth-form' onSubmit={handleSubmit}>
                    <div className='w-full'>
                      <FieldInput
                        autoComplete='email'
                        className='xxl:text-lg h-auto w-full px-4 py-3'
                        data-testid='field_email'
                        id='email'
                        label={<span className='xxl:text-lg'>Email</span>}
                        name='email'
                        placeholder='Enter your email'
                        required
                        type='email'
                      />
                      <br />
                      <FieldInput
                        autoComplete='current-password'
                        className='xxl:text-lg h-auto w-full px-4 py-3 text-base'
                        data-testid='field_password'
                        icon={passwordVisible ? faEye : faEyeSlash}
                        iconPosition='right'
                        id='password'
                        label={<span className='xxl:text-lg'>Password</span>}
                        name='password'
                        onIconClick={() => setPasswordVisible(!passwordVisible)}
                        placeholder='Enter your password'
                        required
                        type={passwordVisible ? 'text' : 'password'}
                      />
                      <Button
                        className='text-primary-default !mt-2 flex h-[20px] justify-end !px-0 !py-0 text-sm'
                        href='/forgotpassword'
                        onClick={() =>
                          trackClickForSegmentAnalytics({
                            ...initClickEventProps,
                            clickText: 'Forgot your password?',
                            clickUrl: `${DEVELOPER_DASHBOARD_URL}/forgotpassword`
                          })
                        }
                        theme='tertiary'
                      >
                        Forgot your password?
                      </Button>
                      <div className='mt-6 flex items-start gap-6'>
                        <LoginSubmission submitting={submitting} valid={!hasValidationErrors} />
                      </div>
                    </div>
                  </form>
                </Fragment>
              )
            }}
            validate={validateLogin}
          />
        </div>
      </div>

      <Footer
        ctaLink={registerLink}
        ctaText='Sign up today'
        description='Not a part of the MetaMask family yet?'
        trackingEvent={{
          ...initClickEventProps,
          clickText: 'Sign up today',
          clickUrl: `${DEVELOPER_DASHBOARD_URL}/register`
        }}
      />
    </div>
  )
}

function Login({ csrfToken }: Readonly<{ csrfToken: string }>) {
  const { isAuthorized } = useUser()
  const { asPath, query } = useRouter()
  const [generateJwt, setGenerateJwt] = useState<boolean>(false)
  const [dashboardSessionToken, setDashboardSessionToken] = useState<string | undefined>()
  const [refreshSession, setRefreshSession] = useState<boolean>(true)
  const [step, setStep] = useState<Infura.AUTH_LOGIN_STEP>(Infura.AUTH_LOGIN_STEP.LOGIN)
  const flags = useFlags()
  const metaMaskLogin = useMemo(() => flags[FEATURE_FLAGS.METAMASK_LOGIN], [flags])

  useEffect(() => {
    const initPageView = async () => {
      window.analytics.page(asPath, {
        page_name: 'Login Page',
        user_experience: metaMaskLogin ? 'B' : 'A'
      })
    }
    if (metaMaskLogin !== undefined && window?.analytics) initPageView()
  }, [metaMaskLogin, asPath])

  useEffect(() => {
    if (query.mm_auth) {
      const loginInfos = JSON.parse(Buffer.from(query.mm_auth as string, 'base64').toString('utf-8') || '{}')
      localStorage.setItem(AUTH_WALLET_SESSION_NAME, loginInfos.mmAuthSession)
      localStorage.setItem(AUTH_WALLET_PAIRING, JSON.stringify(loginInfos.walletPairing))
      setStep(loginInfos.step)
      setGenerateJwt(loginInfos.token)
      setDashboardSessionToken(loginInfos.dashboardSessionToken)
      setRefreshSession(false)
    }
  }, [query.mm_auth])

  useEffect(() => {
    if (isAuthorized) {
      if (query.login_challenge) {
        api.acceptOAuthLogin({ challenge: query.login_challenge as string }).then(({ data: { data } }) => {
          const { redirect_to } = data
          if (redirect_to && (isLocalUrl(redirect_to) || isPermittedUrl(redirect_to))) {
            window.location.href = redirect_to
          } else {
            window.location.href = '/'
          }
        })
      }
    }
  }, [isAuthorized, query.login_challenge, query.redirect])

  const onEmailPasswordLogin = async ({
    email,
    password,
    recaptcha
  }: {
    email: string
    password: string
    recaptcha: string
  }) => {
    const HydraToken = JSON.parse(localStorage.getItem(AUTH_WALLET_SESSION_NAME)!)?.token
    const HydraAccessToken = HydraToken ? HydraToken.accessToken : ''

    return (
      await api.login(
        {
          email,
          password,
          redirect_to: query?.redirect_to as string
        },
        query?.login_challenge as string,
        recaptcha,
        {
          headers: {
            'Cache-Control': 'no-cache',
            csrf_token: csrfToken,
            Pragma: 'no-cache',
            recaptcha_bypass: query.bypass,
            ...(step == Infura.AUTH_LOGIN_STEP.WALLET_LOGIN_EMAIL_PASSWORD && { hydra_token: HydraAccessToken }),
            ...(generateJwt && { token: true })
          }
        }
      )
    ).data
  }

  const onWalletLogin = async ({ recaptcha, user }: { recaptcha?: string; user?: { email: string } }) => {
    const email = user?.email
    const { accessToken, userProfile } = await authenticateAndAuthorize()

    return (
      await api.walletLogin(
        {
          email,
          profileId: userProfile.profileId,
          redirect_to: query?.redirect_to as string
        },
        {
          headers: {
            'Cache-Control': 'no-cache',
            hydra_token: accessToken,
            Pragma: 'no-cache',
            recaptcha,
            recaptcha_bypass: query.bypass,
            refresh_session: refreshSession,
            ...(generateJwt && { token: true })
          }
        }
      )
    ).data
  }
  const onLoginSubmit = async (
    props:
      | {
          authLoginMethod: Infura.AUTH_LOGIN_METHOD.EMAIL_PASSWORD
          email: string
          password: string
          recaptcha: string
        }
      | {
          authLoginMethod: Infura.AUTH_LOGIN_METHOD.WALLET
          recaptcha?: string
          user?: { email: string }
        }
  ) => {
    const { authLoginMethod, ...other } = props
    try {
      if (other[FORM_ERROR]) throw other[FORM_ERROR]

      let loginResponse

      if (authLoginMethod == Infura.AUTH_LOGIN_METHOD.EMAIL_PASSWORD) {
        const { email, password, recaptcha } = props
        loginResponse = await onEmailPasswordLogin({ email, password, recaptcha })
      }

      if (authLoginMethod == Infura.AUTH_LOGIN_METHOD.WALLET) {
        const { recaptcha, user } = props
        loginResponse = await onWalletLogin({ recaptcha, user })
      }

      if (!loginResponse) throw new Error('Something went wrong')

      const { data, session, token } = loginResponse

      if (data.step) {
        localStorage.setItem(AUTH_WALLET_PAIRING, JSON.stringify(data.pairing))
        setStep(data.step)
        return
      }

      if (!data.verified) {
        Router.replace('/resendverify')
        return
      }

      if (needsMfa(data, Number(session?.mfa_verified))) {
        setStep(Infura.AUTH_LOGIN_STEP.MFA_VERIFY)
        return
      }

      if (!data.customer_onboarded) {
        window.location.href = '/welcome'
        return
      }

      if (session?.redirect_to) {
        window.location.href = `${session.redirect_to}${
          token && session.redirect_to !== window.location.origin ? '?token=' + token : ''
        }`
        return
      }

      window.location.href = window.location.origin
    } catch (e: any) {
      return handleSubmissionError(e)
    }
  }

  const onVerifySubmit = async ({ mfaVerificationCode }: { mfaVerificationCode: string }) => {
    if (!mfaVerificationCode) return handleSubmissionError(new Error('Code is required'))
    try {
      const walletSession = JSON.parse(localStorage.getItem(AUTH_WALLET_SESSION_NAME) || '{}')
      const { profileId } = walletSession?.profile || {}
      const userResponse = (
        await api.verifyMFA(
          {
            mfaVerificationCode,
            redirect_to: query?.redirect_to as string,
            ...(profileId && { profileId })
          },
          {
            headers: {
              Authorization: `Bearer ${dashboardSessionToken}`,
              csrf_token: csrfToken,
              refresh_session: refreshSession
            }
          }
        )
      ).data
      const { session, token } = userResponse

      if (session?.redirect_to) {
        window.location.href = `${session.redirect_to}${
          token && session.redirect_to !== window.location.origin ? '?token=' + token : ''
        }`
        return
      }

      window.location.href = window.location.origin
    } catch (e: any) {
      return handleSubmissionError(e)
    }
  }

  const onResetSubmit = async ({ code }: { code: string }) => {
    if (!code) return handleSubmissionError(new Error('Code is required'))
    try {
      const response = (await api.resetMFA({ mfaBackupCode: code })).data

      if (response.session?.redirect_to) {
        window.location.href = response.session?.redirect_to
        return
      }

      window.location.href = window.location.origin
    } catch (error: any) {
      return handleSubmissionError(error)
    }
  }

  return (
    <>
      <Head>
        <link as='script' href={SCRIPT_URL} rel='prefetch' />
      </Head>
      <AuthLayout>
        <>
          <NextSeo
            description='Login. MetaMask Developer provides instant and scalable API access for web3 dapp developers.'
            title='Login | MetaMask Developer'
          />
          <div className='m-auto w-full max-w-3xl px-6 py-8' data-testid='login-form'>
            {step === Infura.AUTH_LOGIN_STEP.LOGIN ? (
              <LoginForm
                csrfToken={csrfToken}
                onSubmit={onLoginSubmit}
                redirectTo={query.redirect_to as string | undefined}
              />
            ) : null}
            {step === Infura.AUTH_LOGIN_STEP.WALLET_LOGIN_EMAIL_PASSWORD ? (
              <LoginWalletLinkingForm onSubmit={onLoginSubmit} setLoginStep={setStep} />
            ) : null}
            {step === Infura.AUTH_LOGIN_STEP.WALLET_LOGIN_MULTI_USER ? (
              <UsersWalletPairingForm onSubmit={onLoginSubmit} />
            ) : null}
            {step === Infura.AUTH_LOGIN_STEP.MFA_VERIFY ? (
              // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
              <VerifyMFAForm onSubmit={onVerifySubmit} setLoginStep={setStep} />
            ) : null}
            {step === Infura.AUTH_LOGIN_STEP.MFA_RESET ? (
              // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
              <ResetMFA onSubmit={onResetSubmit} setLoginStep={setStep} />
            ) : null}
            {step === Infura.AUTH_LOGIN_STEP.MFA_RECOVER ? <RecoverMFA setLoginStep={setStep} /> : null}
          </div>
        </>
      </AuthLayout>
    </>
  )
}

export async function getServerSideProps() {
  return {
    props: { csrfToken: signCsrfToken() }
  }
}

// @ts-ignore
Login._noLayout = true

export default Login
